import { useEffect, useState } from "react";
import { client } from "@/lib/api";

/**
 * useSiteUpdates — powers the navigation "new content" indicators.
 *
 *  Returns an object shaped like `{ videos: Date, podcasts: Date, … }`
 *  with the most recent `created_at`/`published_at` per public section.
 *  Cached in memory for 5 minutes per browser tab so the hook is safe
 *  to mount in <Navigation/>, which renders on every page.
 *
 *  ``hasNewSince(section)`` compares the section's update timestamp
 *  against `localStorage["lastVisit:<section>"]`. Returns true when:
 *    * the user has never visited that section, OR
 *    * the user last visited it before the latest content drop.
 *  Call ``markVisited(section)`` whenever the user clicks the link,
 *  so the badge clears on next render.
 */
const CACHE_KEY = "__juristube_site_updates_cache";
const CACHE_TTL_MS = 5 * 60 * 1000;

let _memo = null; // shared across hook instances

async function fetchUpdates() {
  if (_memo && Date.now() - _memo.ts < CACHE_TTL_MS) return _memo.data;
  try {
    const r = await client.get("/site-updates/last");
    const data = {};
    for (const k of Object.keys(r.data || {})) {
      const v = r.data[k];
      data[k] = v ? new Date(v).getTime() : 0;
    }
    _memo = { ts: Date.now(), data };
    return data;
  } catch {
    return {};
  }
}

export function markVisited(section) {
  try {
    window.localStorage.setItem(`lastVisit:${section}`, String(Date.now()));
  } catch (_) { /* private mode */ }
}

function getLastVisit(section) {
  try {
    const raw = window.localStorage.getItem(`lastVisit:${section}`);
    return raw ? parseInt(raw, 10) : 0;
  } catch { return 0; }
}

export default function useSiteUpdates() {
  const [updates, setUpdates] = useState(_memo?.data || {});
  // bump every minute so a long-open tab notices new content
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    fetchUpdates().then((d) => { if (alive) setUpdates(d); });
    const i = setInterval(() => setTick((t) => t + 1), 60 * 1000);
    return () => { alive = false; clearInterval(i); };
    // tick intentionally not in deps: it forces re-fetch via memo TTL
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch when memo expires
  useEffect(() => {
    if (tick === 0) return;
    fetchUpdates().then(setUpdates);
  }, [tick]);

  const hasNewSince = (section) => {
    const u = updates[section];
    if (!u) return false;
    return u > getLastVisit(section);
  };

  const anyNew = Object.keys(updates).some((s) => hasNewSince(s));

  return { updates, hasNewSince, anyNew, markVisited };
}
